import store, { getStoreState } from 'config/store'
import { receiveCookieDomain } from 'modules/session/actions'
import { setShouldSkipCampaigns } from 'modules/targeting/actions'
import { any, isEmpty, isNil,pathOr } from "ramda"
import { WidgetAlignment } from "shared/constants/frames"
import { isMobileDevice } from 'shared/src/utils/platform'
import { Embed,WidgetPositionType } from 'types/embed'
import { getBrowserContext } from "utils/context"
import { resolveCookieDomain } from 'utils/cookies'
import { broadcast } from "utils/message"
import { normalizeDriftUrl } from "utils/url"

import { getWidgetAlignment } from "./utils"

const DRIFT_DOMAINS = [
  "drift.click",
  "drift.me",
  "drift.com",
  "driftqa.com",
  "driftqa.me",
  "driftqa.click",
]

// Exported for testing
export function isWhitelistedUrl() {
  const browserContext = getBrowserContext()
  const currentUrl = browserContext.url

  const whitelist = pathOr(
    [],
    ["embed", "configuration", "theme", "urlWhitelist"],
    getStoreState()
  )

  if (isEmpty(whitelist) || !currentUrl) {
    return true
  }

  if (any((domain: string) => currentUrl.includes(domain))(DRIFT_DOMAINS)) {
    return true
  }

  const normalizedWhitelist = whitelist.map((url) => normalizeDriftUrl(url))
  if (any((domain: string) => currentUrl.includes(domain))(normalizedWhitelist)) {
    return true
  }

  console.warn(`The Drift widget is not allowed to load on this domain: ${currentUrl}.  Learn more: https://gethelp.drift.com/hc/en-us/articles/360019516853-Troubleshooting-Why-Can-t-I-See-My-Widget-`)
  return false
}

const handleWhitelistedUrls = () => {
  const isWhitelisted = isWhitelistedUrl()

  if (!isWhitelisted) {
    broadcast({ topic: "destroy-drift" })
  }
}

const handleFrameAlignment = (alignment: string = undefined) => {
  const widgetAlignment = alignment || getWidgetAlignment()

  if (widgetAlignment === WidgetAlignment.LEFT) {
    broadcast({
      topic: "align-frames",
      message: { alignment: widgetAlignment },
    })
  }
}

const processCustomIconPlacement = (payload: Embed) => {
  const device = isMobileDevice() ? "mobile" : "desktop"

  const positionType = pathOr(
    WidgetPositionType.DEFAULT,
    ["configuration", "theme", "widgetIconPosition", device, "type"],
    payload
  )

  if (positionType !== WidgetPositionType.DEFAULT) {
    const customPosition = payload.configuration.theme.widgetIconPosition[device].spacing
    const bottomOffset = customPosition.bottom
    broadcast({
      topic: 'update-frame-vertical-offset',
      message: {
        offset: bottomOffset,
      },
    })
  }
}

const overrideVerticalOffset = (offsetValue) => {
  if (isNil(offsetValue)) {
    return
  }

  broadcast({
    topic: 'update-frame-vertical-offset',
    message: {
      offset: offsetValue
    }
  })
}

const overrideHorizontalOffset = (offsetValue) => {
  if (isNil(offsetValue)) {
    return
  }

  broadcast({
    topic: 'update-frame-horizontal-offset',
    message: {
      offset: offsetValue
    }
  })
}

const overrideCookieDomain = (cookieDomain) => {
  if (!cookieDomain) {
    return
  }

  const resolvedCookieDomain = resolveCookieDomain([cookieDomain])
  if (!!resolvedCookieDomain) {
    store.dispatch(receiveCookieDomain({
      cookieDomain: resolvedCookieDomain
    }))
  }
}

export function processPayload(payload: Embed) {
  handleFrameAlignment()
  handleWhitelistedUrls()
  processCustomIconPlacement(payload)
}

export function processOverridePayload(payload: Record<string, any>) {
  if (!payload) {
    return
  }

  Object.keys(payload).forEach((key) => {
    const overrideValue = payload[key]

    switch (key) {
      case 'verticalOffset':
        overrideVerticalOffset(overrideValue)
        break
      case 'horizontalOffset':
        overrideHorizontalOffset(overrideValue)
        break
      case 'cookieDomain':
        overrideCookieDomain(overrideValue)
        break
      case 'campaignsEnabled':
        store.dispatch(setShouldSkipCampaigns(!overrideValue))
        break
      default:
        break
    }
  })
}
